import {
  Button,
  Card,
  Empty,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { CopyOutlined, DownloadOutlined, PrinterOutlined, WhatsAppOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { feeApi, type DefaulterRow } from '@/api/fee';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { MaskedAmount, RevealGroup, RevealToggle } from '@/shared/components/masked-amount';

const ALL_CLASSES = -1; // sentinel — AntD Select dislikes null values, so we use -1 for "All classes"

export function FeeDefaultersPage() {
  const navigate = useNavigate();
  const yearId = useAuthStore((s) => s.academicYearId);
  // undefined = unset; ALL_CLASSES = whole-school sweep; positive number = specific CS
  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [monthsThreshold, setMonthsThreshold] = useState<number>(2);

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const allClasses = classSectionId === ALL_CLASSES;
  const defaultersQuery = useQuery({
    queryKey: ['fee', 'defaulters', yearId, classSectionId, monthsThreshold],
    queryFn: () =>
      feeApi.defaulters({
        academicYearId: yearId ?? undefined,
        classSectionId: allClasses ? undefined : classSectionId,
        monthsThreshold,
      }),
    enabled: classSectionId !== undefined && (allClasses ? yearId != null : true),
  });

  const rows = defaultersQuery.data ?? [];
  const total = rows.reduce((acc, r) => acc + Number(r.outstanding), 0);

  // Class-section subtotals over the FULL dataset (not just the visible page).
  const subtotals = useMemo(() => {
    if (!allClasses) return [];
    const groups = new Map<string, { count: number; outstanding: number }>();
    for (const r of rows) {
      const key = `${r.className} · ${r.sectionName}`;
      const cur = groups.get(key) ?? { count: 0, outstanding: 0 };
      cur.count += 1;
      cur.outstanding += Number(r.outstanding);
      groups.set(key, cur);
    }
    return [...groups.entries()]
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => b.outstanding - a.outstanding);
  }, [rows, allClasses]);

  const copy = (text: string) => navigator.clipboard.writeText(text).catch(() => {});

  const downloadCsv = () => {
    const header = ['Reg #', 'Name', 'Class', 'Section', 'Guardian phone', 'Months due', 'Outstanding (PKR)'];
    const escape = (v: string | number | null | undefined) =>
      v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const lines = [
      header.join(','),
      ...rows.map((r) =>
        [
          r.registrationNumber,
          `${r.firstName} ${r.lastName}`,
          r.className,
          r.sectionName,
          r.primaryGuardianPhone ?? '',
          r.monthsDue,
          r.outstanding,
        ]
          .map(escape)
          .join(','),
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scope = allClasses ? 'all-classes' : `cs-${classSectionId}`;
    a.download = `defaulters-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: TableProps<DefaulterRow>['columns'] = [
    {
      title: 'Reg #',
      dataIndex: 'registrationNumber',
      width: 120,
      render: (rn: string) => <Typography.Text code>{rn}</Typography.Text>,
    },
    {
      title: 'Name',
      render: (_, row) => (
        <Typography.Text strong>{row.firstName} {row.lastName}</Typography.Text>
      ),
    },
    {
      title: 'Class',
      width: 140,
      render: (_, row) => (
        <Tag>{row.className} · {row.sectionName}</Tag>
      ),
    },
    {
      title: 'Guardian',
      width: 240,
      render: (_, row) =>
        row.primaryGuardianPhone ? (
          <Space size={4}>
            <a
              href={`tel:${row.primaryGuardianPhone}`}
              onClick={(e) => e.stopPropagation()}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.primaryGuardianPhone}
            </a>
            <Tooltip title="Copy">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  copy(row.primaryGuardianPhone!);
                }}
              />
            </Tooltip>
            <Tooltip title="WhatsApp">
              <Button
                size="small"
                type="text"
                icon={<WhatsAppOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  const digits = row.primaryGuardianPhone!.replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/${digits}`, '_blank', 'noopener');
                }}
              />
            </Tooltip>
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Months due',
      dataIndex: 'monthsDue',
      width: 110,
      align: 'center',
      render: (n: number) => (
        <Tag color={n >= 3 ? 'error' : n >= 2 ? 'warning' : 'default'}>{n}</Tag>
      ),
      sorter: (a, b) => a.monthsDue - b.monthsDue,
    },
    {
      title: 'Outstanding (PKR)',
      dataIndex: 'outstanding',
      width: 200,
      align: 'right',
      render: (a: string) => (
        <Typography.Text strong>
          <MaskedAmount value={Number(a).toLocaleString('en-PK')} />
        </Typography.Text>
      ),
      sorter: (a, b) => Number(a.outstanding) - Number(b.outstanding),
      defaultSortOrder: 'descend',
    },
  ];

  return (
    // One reveal toggle for the whole defaulters page — table cells + subtotals
    // + the total-outstanding tag all unmask together.
    <RevealGroup>
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title="Defaulters"
        description="Students with months-due ≥ threshold. Click a row to open their cashier page and collect."
        count={
          defaultersQuery.data
            ? { value: defaultersQuery.data.length, label: 'students' }
            : undefined
        }
        actions={
          <Space>
            <RevealToggle style={{ fontSize: 16 }} />
            {defaultersQuery.data && defaultersQuery.data.length > 0 && (
              <>
                {!allClasses && classSectionId !== undefined && (
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={() =>
                      navigate(
                        `/fee/vouchers?classSectionId=${classSectionId}&month=${dayjs().format('YYYY-MM')}`,
                      )
                    }
                  >
                    Print vouchers
                  </Button>
                )}
                <Button icon={<DownloadOutlined />} onClick={downloadCsv}>
                  Export CSV
                </Button>
              </>
            )}
          </Space>
        }
      />

      <Space wrap size={12}>
        <Select
          placeholder="Class · Section"
          value={classSectionId}
          onChange={(v) => setClassSectionId(v)}
          options={[
            { value: ALL_CLASSES, label: 'All classes (whole school)' },
            ...(classSections ?? [])
              .slice()
              .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
              .map((cs) => ({ value: cs.id, label: `${cs.className} · ${cs.sectionName}` })),
          ]}
          style={{ minWidth: 260 }}
          showSearch
          optionFilterProp="label"
        />
        <Space size={6}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Months threshold</Typography.Text>
          <InputNumber min={1} max={12} value={monthsThreshold} onChange={(v) => setMonthsThreshold(Number(v) || 1)} style={{ width: 90 }} />
        </Space>
        <div style={{ flex: 1 }} />
        {defaultersQuery.data && defaultersQuery.data.length > 0 && (
          <Tag color="error" style={{ fontSize: 13 }}>
            Total outstanding · PKR <MaskedAmount value={total.toLocaleString('en-PK')} />
          </Tag>
        )}
      </Space>

      {classSectionId === undefined ? (
        <Empty description="Pick a class · section or 'All classes'" />
      ) : (
        <>
          {/* Subtotals computed from the FULL dataset, not the paginated page. */}
          {allClasses && subtotals.length > 0 && (
            <Card
              size="small"
              title={`By class · section (${subtotals.length} group${subtotals.length === 1 ? '' : 's'})`}
              styles={{ body: { padding: 0 } }}
            >
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                showHeader={false}
                dataSource={subtotals}
                columns={[
                  { title: 'Class', dataIndex: 'key' },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    width: 140,
                    align: 'right',
                    render: (n: number) => `${n} student${n === 1 ? '' : 's'}`,
                  },
                  {
                    title: 'Outstanding',
                    dataIndex: 'outstanding',
                    width: 200,
                    align: 'right',
                    render: (n: number) => (
                      <Typography.Text strong>
                        PKR <MaskedAmount value={n.toLocaleString('en-PK')} />
                      </Typography.Text>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          <Card styles={{ body: { padding: 0 } }}>
            <Table
              rowKey="enrollmentId"
              size="middle"
              loading={defaultersQuery.isFetching}
              columns={columns}
              dataSource={rows}
              pagination={allClasses ? false : { pageSize: 25, showSizeChanger: true }}
              scroll={allClasses ? { y: 600 } : undefined}
              onRow={(row) => ({
                onClick: () => navigate(`/fee/cashier/${row.enrollmentId}`),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </>
      )}
    </div>
    </RevealGroup>
  );
}
